import { resolveSkillClass, MAGNUM_CLASSES, WEAPON_CLASS_ORDER } from './conventionResults';

// Decide where a same-Lag drop lands relative to the hovered card, by DIRECTION
// rather than by pointer half. Dragging down onto a later card = after it, dragging
// up onto an earlier card = before it — so the dragged shooter always takes that
// card's standplass and every drop on another card changes the order. The old
// midpoint rule left half of the neighbouring card as a dead zone that reproduced
// the current order (adjacent swaps "did nothing") and landed long moves one slot
// short depending on a few pixels.
export function dropPlaceAfter(fromIndex, targetIndex) {
    return targetIndex > fromIndex;
}

// Move a participant within a Lag's ordered array.
//   participants  - the current ordered array (not mutated)
//   fromIndex     - index of the dragged participant in the ORIGINAL array
//   targetIndex   - index of the card it was dropped onto, in the ORIGINAL array
//   placeAfter    - true to insert after the target card (see dropPlaceAfter)
// Returns a NEW array. Out-of-range fromIndex returns an unchanged copy.
export function reorderParticipants(participants, fromIndex, targetIndex, placeAfter = false) {
    const out = Array.isArray(participants) ? [...participants] : [];
    if (out.length === 0) return out;
    if (fromIndex < 0 || fromIndex >= out.length) return out;

    const [moved] = out.splice(fromIndex, 1);
    // targetIndex was computed against the original array. Removing `fromIndex`
    // shifts every later index down by one, so compensate before inserting.
    let insertAt = targetIndex;
    if (fromIndex < targetIndex) insertAt -= 1;
    if (placeAfter) insertAt += 1;
    insertAt = Math.max(0, Math.min(insertAt, out.length));
    out.splice(insertAt, 0, moved);
    return out;
}

// Client-side "locate" predicate for the kanban quick-search. Matches a shooter by
// name substring, club (sportsClub) substring, or by exact skytterID (nsfId) when the
// query is purely numeric. nsfId/sportsClub may come from the participant snapshot or
// be merged in from the enriched user record by the caller.
export function participantMatchesLocate(participant, query) {
    if (!participant) return false;
    const q = String(query || '').trim().toLowerCase();
    if (!q) return false;

    const first = String(participant.firstname || '').toLowerCase();
    const last = String(participant.lastname || '').toLowerCase();
    const full = `${first} ${last}`.trim();
    const club = String(participant.sportsClub || '').toLowerCase();

    if (/^\d+$/.test(q) && participant.nsfId && String(participant.nsfId) === q) {
        return true;
    }
    return first.includes(q) || last.includes(q) || full.includes(q) || club.includes(q);
}

// Resolve the skytterklasse to SNAPSHOT onto a participant at add-time. Manual
// admin selection wins; otherwise we resolve from the shooter's classification for
// the chosen weapon. "Ingen klasse" (unmappable weapon) and norgesfelt resolve to
// null so we never store a misleading class; the legitimate NSF default "D"
// (unclassified shooter in a known discipline) is kept.
export function snapshotSkillClass({ weaponClass, manualSkillClass, user, stage, classMap, disciplineMap }) {
    if (manualSkillClass) return manualSkillClass;
    // resolveSkillClass expects a participant-shaped object whose `.class` is the weapon.
    const resolved = resolveSkillClass(
        { class: weaponClass },
        user,
        stage,
        classMap,
        disciplineMap,
    );
    if (!resolved || resolved === 'Ingen klasse') return null;
    return resolved;
}

// Label for the participants whose club is unknown/blank — also the value the kanban
// club pill carries, so the locate filter can special-case "no club".
export const NO_CLUB_LABEL = 'Uten klubb';

// Summarize a convention's Lag participants for the kanban header:
//   uniqueShooters - distinct participants across all stages (one person counted once)
//   totalStarts    - every (participant, stage) entry (1 participant in 1 Lag = 1 start)
//   byClub         - unique shooters per club, blank club bucketed as NO_CLUB_LABEL,
//                    sorted by shooter count desc, then club name (Norwegian collation).
// stageParticipantLists: array of per-stage participant arrays.
// clubOf: (participant) => club name string ('' when unknown).
export function summarizeKanban(stageParticipantLists, clubOf) {
    const shooterIds = new Set();
    const clubShooters = new Map();
    const classShooters = new Map();
    let totalStarts = 0;
    (stageParticipantLists || []).forEach((list) => {
        (list || []).forEach((p) => {
            totalStarts += 1;
            shooterIds.add(p.id);
            const club = (clubOf ? clubOf(p) : p.sportsClub) || NO_CLUB_LABEL;
            if (!clubShooters.has(club)) clubShooters.set(club, new Set());
            clubShooters.get(club).add(p.id);
            const cls = normalizeClass(p.class);
            if (cls) {
                if (!classShooters.has(cls)) classShooters.set(cls, new Set());
                classShooters.get(cls).add(p.id);
            }
        });
    });
    const byClub = [...clubShooters.entries()]
        .map(([club, ids]) => ({ club, shooters: ids.size }))
        .sort((a, b) => b.shooters - a.shooters || a.club.localeCompare(b.club, 'nb'));
    // Weapon classes in the fixed display order; codes outside it go last, alphabetically.
    const classRank = (cls) => {
        const i = WEAPON_CLASS_ORDER.indexOf(cls);
        return i < 0 ? WEAPON_CLASS_ORDER.length : i;
    };
    const byClass = [...classShooters.entries()]
        .map(([cls, ids]) => ({ cls, shooters: ids.size }))
        .sort((a, b) => classRank(a.cls) - classRank(b.cls) || a.cls.localeCompare(b.cls, 'nb'));
    return { uniqueShooters: shooterIds.size, totalStarts, byClub, byClass };
}

function normalizeClass(value) {
    return String(value || '').trim().toLowerCase();
}

// Class filter predicate for the kanban. An empty selection means the filter is
// inactive and matches everyone; otherwise the participant's weapon class must be
// one of the selected codes. A participant without a class never matches an active filter.
export function participantMatchesClass(participant, selectedClasses) {
    const selected = Array.isArray(selectedClasses) ? selectedClasses.map(normalizeClass).filter(Boolean) : [];
    if (selected.length === 0) return true;
    const cls = normalizeClass(participant && participant.class);
    return !!cls && selected.includes(cls);
}

// ---------------------------------------------------------------------------
// Arrangørlag (organizer Lag)
//
// A Lag for the arranging club's own shooters. It is held the DAY BEFORE the
// main competition, so it carries the day-before date — but it shares the one
// continuous "Lag 1..x" numbering with every other Lag. To keep that numbering
// chronological, arrangørlag live as a contiguous block at the FRONT of
// convention.stages and a new one is INSERTED there rather than appended.
//
// Participants are stored positionally (stages[i] ↔ convention.participants{i+1}),
// so an insert has to shift every slot from the insert point up by one. That is
// the mirror image of the shift deleteStage() already performs.
// ---------------------------------------------------------------------------

// A Lag is an arrangørlag when it carries the organizerStage marker. Stored as
// 1/0 like stageClosed/stageDone, so parse rather than trust truthiness.
export function isOrganizerStage(stage) {
    if (!stage) return false;
    return parseInt(stage.organizerStage, 10) > 0;
}

// Partition the positional participant lists (parallel to stages, as in
// summarizeKanban()) into arrangørlag lists and main-competition lists, so the
// header can count "external" shooters (main Lag) apart from the arranging
// club's own day-before Lag. Returns NEW arrays; a stage without a list yields [].
export function splitStageLists(stages, stageParticipantLists) {
    const organizerLists = [];
    const mainLists = [];
    (Array.isArray(stages) ? stages : []).forEach((stage, idx) => {
        const list = (stageParticipantLists || [])[idx] || [];
        (isOrganizerStage(stage) ? organizerLists : mainLists).push(list);
    });
    return { organizerLists, mainLists };
}

// Signup-fee income for the kanban header. Arrangørlag shooters never pay, so
// only main-Lag starts count, at feePrice (signupProductPrice, incl VAT) each.
//   mainLists     - per-main-Lag participant arrays (splitStageLists().mainLists)
//   feePrice      - kr per start; 0/unset gives counts only and zero amounts
//   paidStartsFor - (participant) => paid-start count (top-level paidStartsCount)
//   perStart      - true (default, competitions): one fee per main-Lag start;
//                   false (trainings): one fee per shooter however many Lag
// A shooter's paid starts are capped at their main-Lag starts, so an over-paid
// or Vipps-paid-in-advance shooter never inflates the paid figure. Amounts are
// rounded to øre.
export function summarizeSignupIncome({
    mainLists, feePrice, paidStartsFor, perStart = true,
} = {}) {
    const price = Number(feePrice) > 0 ? Number(feePrice) : 0;
    const startsById = new Map();
    const participantById = new Map();
    (Array.isArray(mainLists) ? mainLists : []).forEach((list) => {
        (list || []).forEach((p) => {
            if (!p) return;
            const lagStarts = (startsById.get(p.id) || 0) + 1;
            startsById.set(p.id, perStart ? lagStarts : Math.min(lagStarts, 1));
            if (!participantById.has(p.id)) participantById.set(p.id, p);
        });
    });
    let starts = 0;
    let paidStarts = 0;
    startsById.forEach((count, id) => {
        starts += count;
        const paid = typeof paidStartsFor === 'function' ? Number(paidStartsFor(participantById.get(id))) || 0 : 0;
        paidStarts += Math.min(Math.max(paid, 0), count);
    });
    const unpaidStarts = starts - paidStarts;
    const round2 = n => Math.round(n * 100) / 100;
    return {
        starts,
        paidStarts,
        unpaidStarts,
        expectedAmount: round2(starts * price),
        paidAmount: round2(paidStarts * price),
        unpaidAmount: round2(unpaidStarts * price),
    };
}

// Where the next arrangørlag goes: immediately after the leading block of
// arrangørlag, i.e. before the first main-competition Lag. Scanning the leading
// block (rather than counting every flagged Lag) keeps the new one in the block
// even if an older convention has a stray flag further down the list.
export function organizerInsertIndex(stages) {
    const list = Array.isArray(stages) ? stages : [];
    let i = 0;
    while (i < list.length && isOrganizerStage(list[i])) {
        i += 1;
    }
    return i;
}

// Start time for the next Lag of either kind. Returns a Date, or null when the
// convention has nothing to base a start on (no previous Lag date, no startDate)
// — callers decide whether to hide the preview or fall back to "now".
//
//   organizer=false  previous MAIN Lag + stageInterval, else the convention start.
//                    Arrangørlag are skipped: chaining off one would date the new
//                    main Lag to the day before.
//   organizer=true   previous ARRANGØRLAG + stageInterval, else the day before the
//                    convention start at the same clock time.
export function nextStageDate({ stages, startDate, stageInterval, organizer = false } = {}) {
    const list = Array.isArray(stages) ? stages : [];
    const interval = stageInterval || 10;

    const sameKind = list.filter(s => isOrganizerStage(s) === !!organizer);
    const prev = sameKind[sameKind.length - 1];
    if (prev && prev.date) {
        const next = new Date(prev.date);
        next.setMinutes(next.getMinutes() + interval);
        return next;
    }

    // No previous Lag of this kind — anchor on the convention start. Fall back to
    // the first Lag's date for conventions saved without a startDate.
    const anchor = startDate || list.find(s => s.date)?.date;
    if (!anchor) {
        return null;
    }
    const base = new Date(anchor);
    if (organizer) {
        // setDate() works in local time, so the clock time of the stevnestart is
        // preserved and month/year boundaries roll over on their own.
        base.setDate(base.getDate() - 1);
    }
    return base;
}

// Plan an insert into the positional stages array.
// Returns a NEW array (the input is never touched) with every Lag re-stamped
// `Lag N` / idx N so the numbering runs 1..x left to right, plus the participant
// slot moves the caller must apply. Moves are 1-based slot numbers listed
// HIGHEST FIRST, so applying them in order never overwrites a slot still needed.
export function buildStageInsert(stages, insertIndex, newStage) {
    const list = Array.isArray(stages) ? stages : [];
    const at = Math.max(0, Math.min(insertIndex, list.length));

    const inserted = [...list.slice(0, at), newStage, ...list.slice(at)];
    const renumbered = inserted.map((stage, i) => ({
        ...stage,
        name: `Lag ${i + 1}`,
        idx: i + 1,
    }));

    const participantMoves = [];
    for (let slot = list.length; slot >= at + 1; slot -= 1) {
        participantMoves.push({ from: slot, to: slot + 1 });
    }

    return { stages: renumbered, participantMoves };
}

// A copied Lag starts this many hours after the Lag it was copied from.
export const ORGANIZER_COPY_HOURS = 2;

// Start time for a COPY of an existing Lag: the source start plus `hours`.
// Returns a Date, or null when the source has no usable date — callers refuse the
// copy rather than invent a time. setHours() makes this a WALL-CLOCK offset, the
// same local-time reasoning nextStageDate() uses for the day-before shift.
export function copyStageStart(date, hours = ORGANIZER_COPY_HOURS) {
    if (!date) return null;
    const next = new Date(date);
    if (Number.isNaN(next.getTime())) return null;
    next.setHours(next.getHours() + hours);
    return next;
}

// ---------------------------------------------------------------------------
// Standplass (a shooter's 1-based firing point within a Lag)
//
// An entry may carry an explicit `standplass` number so a Lag can be 1.2.3.x.x.6
// with open firing points in between. Entries without it (every Lag built before
// this existed) use their index + 1, so old conventions read exactly as before.
// Every reader that shows a shooter's number goes through these helpers — never
// through the array index directly.
//
// A shooter doing several Lag normally wants the SAME standplass every time, so
// while an admin fills a new Lag the kanban shows what that shooter had on their
// nearest OTHER Lag.
// ---------------------------------------------------------------------------

// The firing point of one entry: the stored number when it is a positive
// integer, otherwise the legacy index + 1.
export function effectiveStandplass(participant, idx) {
    const stored = parseInt(participant && participant.standplass, 10);
    return stored >= 1 ? stored : (idx || 0) + 1;
}

// The Lag as [{ participant, position }] sorted by firing point. A stored
// number that collides with an earlier entry is bumped to the next free point
// (defensive — the board never writes duplicates), so no shooter disappears.
export function stageEntries(participants) {
    const list = Array.isArray(participants) ? participants : [];
    const wanted = list
        .map((participant, idx) => ({ participant, position: effectiveStandplass(participant, idx), idx }))
        .sort((a, b) => a.position - b.position || a.idx - b.idx);
    const taken = new Set();
    return wanted.map(({ participant, position }) => {
        let p = position;
        while (taken.has(p)) p += 1;
        taken.add(p);
        return { participant, position: p };
    });
}

// How many firing points the board draws for a Lag: the configured stageSlots,
// or more when an entry sits above it. A Lag without a limit always shows one
// open point after the last shooter so there is somewhere to drop.
export function stageSlotCount(participants, stageSlots) {
    const entries = stageEntries(participants);
    const highest = entries.length ? entries[entries.length - 1].position : 0;
    const limit = parseInt(stageSlots, 10) > 0 ? parseInt(stageSlots, 10) : 0;
    if (limit === 0) return highest + 1;
    return Math.max(limit, highest);
}

// The firing points of a Lag in order: [{ position, participant|null }].
export function buildStageSlots(participants, stageSlots) {
    const byPosition = new Map();
    stageEntries(participants).forEach(e => byPosition.set(e.position, e.participant));
    const count = stageSlotCount(participants, stageSlots);
    const slots = [];
    for (let position = 1; position <= count; position += 1) {
        slots.push({ position, participant: byPosition.get(position) || null });
    }
    return slots;
}

// First open firing point — fills a gap before extending the Lag.
export function lowestFreeStandplass(participants) {
    const taken = new Set(stageEntries(participants).map(e => e.position));
    let p = 1;
    while (taken.has(p)) p += 1;
    return p;
}

function entryKeyMatches(participant, key) {
    if (!participant) return false;
    if (participant.md5 && participant.md5 === key) return true;
    return participant.id !== undefined && participant.id !== null && participant.id === key;
}

// Put one shooter (by md5, or id for entries without md5) on `position`. An
// occupant of that point takes the shooter's old point (swap). Every entry
// comes back with an explicit `standplass` so the gap survives later edits,
// sorted by firing point. Returns an unchanged copy for an unknown key or a
// position below 1. Never mutates the input.
export function assignStandplass(participants, key, position) {
    const list = Array.isArray(participants) ? participants : [];
    const target = parseInt(position, 10);
    if (!(target >= 1) || !list.some(p => entryKeyMatches(p, key))) {
        return list.map(p => ({ ...p }));
    }
    const entries = stageEntries(list);
    const moving = entries.find(e => entryKeyMatches(e.participant, key));
    const occupant = entries.find(e => e.position === target && e !== moving);
    const from = moving.position;
    const out = entries.map((e) => {
        let standplass = e.position;
        if (e === moving) standplass = target;
        else if (e === occupant) standplass = from;
        return { ...e.participant, standplass };
    });
    return out.sort((a, b) => a.standplass - b.standplass);
}

// Every Lag each shooter is on, keyed by user id:
//   { [userId]: [{ stageIdx, md5, name, position }] }   — board order, position = firing point
// stageParticipantLists is parallel to stages, as in summarizeKanban().
export function buildStandplassIndex(stages, stageParticipantLists) {
    const index = {};
    (stages || []).forEach((stage, stageIdx) => {
        const list = (stageParticipantLists || [])[stageIdx] || [];
        stageEntries(list).forEach(({ participant, position }) => {
            const userId = participant?.id;
            if (!userId) return;
            if (!index[userId]) index[userId] = [];
            index[userId].push({
                stageIdx,
                md5: stage?.md5,
                name: stage?.name,
                position,
            });
        });
    });
    return index;
}

// The standplass to aim for when placing `userId` on the Lag at `targetStageIdx`:
// their standplass on the nearest OTHER Lag — the last one BEFORE the target, or
// the first one after it when they are on no earlier Lag (an admin filling Lag 5
// before Lag 2 is just as well served by the later Lag). Board order decides
// "nearest": it is the Lag 1..x order the admin reads across the screen, and it
// survives a Lag having its time edited.
//
// Returns the index entry ({ stageIdx, md5, name, position }) or null when the
// shooter is on no other Lag.
export function referenceStandplass(index, userId, targetStageIdx) {
    const entries = (index && index[userId]) || [];
    const others = entries.filter(entry => entry.stageIdx !== targetStageIdx);
    if (others.length === 0) return null;
    let before = null;
    others.forEach(entry => {
        if (entry.stageIdx < targetStageIdx) before = entry;
    });
    return before || others[0];
}

// ---------------------------------------------------------------------------
// Magnum spacing
//
// A Lag shoots magnum LAST: the non-magnum shooters go first, then they step back
// and the magnum shooters shoot the Lag alone. So the only spacing that matters is
// magnum-to-magnum — they need 2 m, which is one free standplass between them.
//
// A magnum shooter standing next to a fin/grov/rev shooter is therefore fine: that
// neighbour has stepped back by the time the magnum shooter fires. Only two magnum
// shooters on NEIGHBOURING standplasser are a problem.
//
// The board warns; it never blocks — the desk decides, and a single drag one place
// along clears it.
// ---------------------------------------------------------------------------

function isMagnumClass(weaponClass) {
    return MAGNUM_CLASSES.includes(String(weaponClass || '').trim().toLowerCase());
}

// Every place in a Lag where two magnum shooters sit next to each other.
//   pairs     - [{ from, to }] 1-based standplass numbers, to === from + 1
//   positions - every 1-based standplass involved, ascending and deduped, so three
//               magnum shooters in a row report two pairs and three positions.
// Returns empty arrays for a Lag with no magnum problem.
export function magnumAdjacency(participants) {
    const entries = stageEntries(participants);
    const pairs = [];
    const positions = [];
    for (let i = 0; i + 1 < entries.length; i += 1) {
        const a = entries[i];
        const b = entries[i + 1];
        // Firing points, not indexes: 1.x.3 has an open point between them.
        if (b.position === a.position + 1
            && isMagnumClass(a.participant?.class) && isMagnumClass(b.participant?.class)) {
            pairs.push({ from: a.position, to: b.position });
            if (positions[positions.length - 1] !== a.position) positions.push(a.position);
            positions.push(b.position);
        }
    }
    return { pairs, positions };
}

// ---------------------------------------------------------------------------
// Queue column
// ---------------------------------------------------------------------------

function sameLocalDay(a, b) {
    const d1 = new Date(a);
    const d2 = new Date(b);
    if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return false;
    return d1.getFullYear() === d2.getFullYear()
        && d1.getMonth() === d2.getMonth()
        && d1.getDate() === d2.getDate();
}

// The shooters to show in the "Kø / ikke fordelt" column.
//   participants  - convention.participants (everyone signed up to the convention)
//   lagCountById  - { [userId]: number of Lag they stand on }
//   isCompetition - convention.isCompetition
//   startDate     - convention.startDate: the training's day
//   now           - fallback day when the convention has no startDate (wall clock)
// Competitions: only shooters on no Lag — once placed they leave the queue.
// Trainings: a shooter does several Lag the same evening, so everyone who signed
// up on the TRAINING'S day (signupDate vs startDate, local date) stays in the
// queue however many Lag they are already on, and the desk drags them onto the
// next Lag from there. The convention's day, never the viewer's clock: looked
// at the morning after, 435 (2026-10-22) had lost all 12 placed shooters.
// Shooters on no Lag are kept too, whatever their signup date, so nobody goes
// missing. Returns a new array in participants order.
export function queueParticipantsFor({
    participants, lagCountById = {}, isCompetition = false, startDate = null, now = new Date(),
} = {}) {
    const day = startDate && !Number.isNaN(new Date(startDate).getTime()) ? startDate : now;
    return (Array.isArray(participants) ? participants : []).filter((p) => {
        if (!p) return false;
        if (!lagCountById[p.id]) return true;
        if (isCompetition) return false;
        return !!p.signupDate && sameLocalDay(p.signupDate, day);
    });
}
